import { Date_dateOnly } from "../../common/dateUtils";
import { CodeStatus, Location, PermitType, SignatureType, WorkOrigin } from "../../common/enums";
import positionUtils from "../../common/positionUtils";
import { formElement } from "../forms/FormElement";
import DivingPermitFormState from "../forms/Permits/DivingPermitFormState";
import EnergyHarbourPermitFormState, {
    EnergyHarbourPermitFormState_default
} from "../forms/Permits/EnergyHarbourPermit/EnergyHarbourPermitFormState";
import Person, { Person_default, Person_toFormElement, Person_toParticipantListItem } from "../Person";
import Position, { Position_toPositionPickerRow } from "../Position";
import Signature, { Signature_normalize } from "../Signature";
import { permitContentVersions } from "../WorkPermit";

type DivingPermit = {
    remindMe: boolean;
    remindMeDate: Date;
    id: string;
    entityId: number;
    startDate: Date;
    endDate: Date;
    description: string;
    status: CodeStatus;
    positions: Position[];
    sequenceIdentifier: string;
    persons: Person[];
    permitName: PermitType.DivingPermit;
    createdByUserName: string;
    expirationReminderEmail: string | undefined;
    expirationReminderPhoneNumber: string | undefined;
    version: number;
    hasAttachedFiles: boolean;
    entrepreneur: string;
    renewalOfPermitSequenceIdentifier: string;
    initialStartDate: Date;
    renewedByPermitSequenceIdentifier: string;
    extendedEndDate: Date;
    divingLeader: Person;
    projectLeader: Person;
    VHFIsAvailable: boolean;
    divingLocation: Location;
    divingFrom: WorkOrigin;
    workInAccordanceWithRegulations: boolean;
    energyHarbourSequenceIdentifier: string;
    departmentId: number;
    rejectionComment: string;
    adminComment: string;
    divingCompanyNr: string;

    apmtInformationConfirmationSignature: Signature;
    permitProviderSignature: Signature;

    contentVersion: number;

    canUpdate: boolean;
    canAccept: boolean;
    canCheckout: boolean;
    canRenew: boolean;
    canManageAttachments: boolean;
    canManageParticipants: boolean;
    canManageAdminComment: boolean;
    canManagePositions: boolean;
    canReject: boolean;
    canPrefillEnergyHarbourPermit: boolean;
    canManagePositionPolygons: boolean;
};

export default DivingPermit;

export function DivingPermit_toFormState(divingPermit: DivingPermit): DivingPermitFormState {
    const formState: DivingPermitFormState = {
        adminComment: formElement(divingPermit.adminComment),
        description: formElement(divingPermit.description),
        endDate: formElement(divingPermit.endDate),
        entrepreneur: formElement(divingPermit.entrepreneur),
        expirationReminderEmail: formElement(divingPermit.expirationReminderEmail ?? ""),
        expirationReminderPhoneNumber: formElement(divingPermit.expirationReminderPhoneNumber ?? ""),
        positions: divingPermit.positions.map(Position_toPositionPickerRow),
        remindMe: formElement(divingPermit.remindMe),
        remindMeDate: formElement(divingPermit.remindMeDate),
        startDate: formElement(divingPermit.startDate),
        VHFIsAvailable: formElement(divingPermit.VHFIsAvailable),
        divingLocation: formElement(divingPermit.divingLocation),
        divingFrom: formElement(divingPermit.divingFrom),
        divingLeader: Person_toFormElement(divingPermit.divingLeader),
        energyHarbourSequenceIdentifier: formElement(divingPermit.energyHarbourSequenceIdentifier),
        persons: divingPermit.persons.map(Person_toParticipantListItem),
        projectLeader: Person_toFormElement(divingPermit.projectLeader),
        divingCompanyNr: formElement(divingPermit.divingCompanyNr),
        apmtInformationConfirmationSignature: formElement(""),
        apmtSignatureNameClarification: formElement("")
    };

    return formState;
}

export function DivingPermit_toEnergyHarbourPermitFormState(divingPermit: DivingPermit): EnergyHarbourPermitFormState {
    const prefilledFields = {
        remindMe: formElement(divingPermit.remindMe),
        remindMeDate: formElement(divingPermit.remindMeDate),
        startDate: formElement(Date_dateOnly(divingPermit.startDate)),
        endDate: formElement(Date_dateOnly(divingPermit.endDate)),
        description: formElement(divingPermit.description),
        entrepreneur: formElement(divingPermit.entrepreneur),
        projectLeader: Person_toFormElement(divingPermit.projectLeader),
        positions: divingPermit.positions.map((p) => {
            const positionPickerRow = Position_toPositionPickerRow(p);
            positionPickerRow.id = 0;
            positionUtils.addPositionOffset(positionPickerRow);

            return positionPickerRow;
        }),
        expirationReminderEmail: formElement(divingPermit.expirationReminderEmail),
        expirationReminderPhoneNumber: formElement(divingPermit.expirationReminderPhoneNumber),
        persons: divingPermit.persons.map((p, i) => ({
            index: i,
            isActive: p.isActive,
            name: p.name,
            phoneNumber: p.phoneNumber
        }))
    };

    const defaultFormState = EnergyHarbourPermitFormState_default();

    const energyHarbourPermitFormState: EnergyHarbourPermitFormState = {
        ...defaultFormState,
        riskIdentifications: {
            ...defaultFormState.riskIdentifications,
            isDivingIncludedInPrint: formElement(true),
            divingChecklist: {
                ...defaultFormState.riskIdentifications.divingChecklist,
                divingPermitSequenceIdentifier: formElement(divingPermit.sequenceIdentifier)
            }
        },
        ...prefilledFields
    };

    return energyHarbourPermitFormState;
}

export function DivingPermit_normalize(permit: DivingPermit): DivingPermit {
    return {
        positions: permit.positions.sort((a, b) => a.order - b.order),
        remindMeDate: new Date(permit.remindMeDate),
        startDate: new Date(permit.startDate),
        endDate: new Date(permit.endDate),
        initialStartDate: new Date(permit.initialStartDate),
        extendedEndDate: new Date(permit.extendedEndDate),
        apmtInformationConfirmationSignature: Signature_normalize(
            permit.apmtInformationConfirmationSignature,
            SignatureType.APMTInformationConfirmation
        ),
        permitProviderSignature: Signature_normalize(permit.permitProviderSignature, SignatureType.PermitProvider),
        adminComment: permit.adminComment ?? "",
        canAccept: permit.canAccept ?? false,
        canCheckout: permit.canCheckout ?? false,
        canManageAttachments: permit.canManageAttachments ?? false,
        canManageAdminComment: permit.canManageAdminComment ?? false,
        canManageParticipants: permit.canManageParticipants ?? false,
        canManagePositions: permit.canManagePositions ?? false,
        canReject: permit.canReject ?? false,
        createdByUserName: permit.createdByUserName ?? "",
        canRenew: permit.canRenew ?? false,
        departmentId: permit.departmentId ?? 0,
        description: permit.description ?? "",
        canUpdate: permit.canUpdate ?? false,
        divingCompanyNr: permit.divingCompanyNr ?? "",
        divingFrom: permit.divingFrom ?? WorkOrigin.Quay,
        divingLeader: permit.divingLeader ?? Person_default(),
        divingLocation: permit.divingLocation ?? Location.General,
        energyHarbourSequenceIdentifier: permit.energyHarbourSequenceIdentifier ?? "",
        entityId: permit.entityId ?? 0,
        entrepreneur: permit.entrepreneur ?? "",
        expirationReminderEmail: permit.expirationReminderEmail ?? "",
        expirationReminderPhoneNumber: permit.expirationReminderPhoneNumber ?? "",
        id: permit.id ?? "",
        hasAttachedFiles: permit.hasAttachedFiles ?? false,
        permitName: permit.permitName ?? PermitType.DivingPermit,
        persons: permit.persons ?? [],
        projectLeader: permit.projectLeader ?? Person_default(),
        remindMe: permit.remindMe ?? false,
        rejectionComment: permit.rejectionComment ?? "",
        renewalOfPermitSequenceIdentifier: permit.renewalOfPermitSequenceIdentifier ?? "",
        renewedByPermitSequenceIdentifier: permit.renewedByPermitSequenceIdentifier ?? "",
        sequenceIdentifier: permit.sequenceIdentifier ?? "",
        status: permit.status ?? CodeStatus.Registered,
        version: permit.version ?? 0,
        workInAccordanceWithRegulations: permit.workInAccordanceWithRegulations ?? false,
        VHFIsAvailable: permit.VHFIsAvailable ?? false,
        canPrefillEnergyHarbourPermit: permit.canPrefillEnergyHarbourPermit ?? false,
        canManagePositionPolygons: permit.canManagePositionPolygons ?? false,
        contentVersion: permit.contentVersion ?? Math.max(...Object.values(permitContentVersions))
    };
}
